import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { MeResponse } from '@billspilt/shared';
import { api, ApiError } from '@/lib/api';

const STORAGE_KEY = 'billspilt:auth';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days, matches server session TTL

type Cached = {
  user: MeResponse['user'];
  identities: MeResponse['identities'];
  cachedAt: number;
};

function readCache(): Cached | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Cached;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(c: Cached | null) {
  try {
    if (c) localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / quota */
  }
}

export const useAuthStore = defineStore('auth', () => {
  const cached = readCache();
  const user = ref<MeResponse['user'] | null>(cached?.user ?? null);
  const identities = ref<MeResponse['identities']>(cached?.identities ?? []);
  const validating = ref(false);

  /** Hits the server, refreshes the cache, and returns whether the user is logged in. */
  async function fetchMe(): Promise<boolean> {
    validating.value = true;
    try {
      const me = await api.get<MeResponse>('/api/auth/line/me');
      user.value = me.user;
      identities.value = me.identities;
      writeCache({ user: me.user, identities: me.identities, cachedAt: Date.now() });
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        user.value = null;
        identities.value = [];
        writeCache(null);
        return false;
      }
      // Network error: keep the cached value so the UI doesn't snap back to logged-out.
      return user.value != null;
    } finally {
      validating.value = false;
    }
  }

  /** Fire-and-forget revalidation; safe to call on every navigation. */
  function revalidate() {
    void fetchMe().catch(() => {});
  }

  function startLineLogin(next?: string) {
    // CRITICAL: must hit the API origin, not the Pages origin. When the two
    // are split (e.g. billspilt.treexhd.me ↔ billspilt-api.treexhd.me),
    // hitting `/api/...` on the Pages host returns the SPA shell — a blank
    // page that swallows the click silently. Use VITE_API_BASE_URL when set.
    const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '') as string;
    const target = apiBase
      ? `${apiBase.replace(/\/+$/, '')}/api/auth/line/start`
      : '/api/auth/line/start';
    const url = new URL(target, location.origin);
    if (next) url.searchParams.set('next', next);
    location.href = url.toString();
  }

  /** Server-side revoke + local cache wipe. */
  async function logout() {
    try {
      await api.post('/api/auth/line/logout');
    } catch {
      /* even if the request fails, we still want local state cleared */
    }
    user.value = null;
    identities.value = [];
    writeCache(null);
  }

  /** Wipe the localStorage cache without touching the server cookie. */
  function clearLocalCache() {
    user.value = null;
    identities.value = [];
    writeCache(null);
  }

  return {
    user,
    identities,
    validating,
    fetchMe,
    revalidate,
    startLineLogin,
    logout,
    clearLocalCache,
  };
});
